// components/Header.js
import React, { useState, useEffect } from 'react';
import { Trophy, RotateCcw, Timer } from 'lucide-react';

const Header = ({ appState, sessionConfig, sessionType, onReset }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getSessionTitle = () => {
    if (sessionType === 'mlp') {
      return 'MLP Tournament';
    }
    return 'SMART Open Play';
  };

  const getSessionSubtitle = () => {
    if (sessionType === 'mlp') {
      return 'Major League Pickleball Format';
    }
    
    if (sessionConfig?.competitionLevel) {
      const levels = {
        'recreational': 'Recreational Play',
        'competitive': 'Competitive Play', 
        'tournament': 'Tournament Play',
        'professional': 'Professional Play',
        'no-scoring': 'Social Play (No Scoring)'
      };
      return levels[sessionConfig.competitionLevel] || 'League Play';
    }
    
    return 'Dynamic Rotation System';
  };

  const showTimer = () => {
    // Show timer for SMART sessions when active
    if (sessionType === 'smart' && appState === 'playing' && sessionConfig?.endTime) {
      return true;
    }
    return false;
  };

  const getTimeRemaining = () => {
    if (!sessionConfig?.endTime) return '';
    
    const now = Date.now();
    const remaining = sessionConfig.endTime - now;
    
    if (remaining <= 0) return 'TIME EXPIRED';
    
    const minutes = Math.floor(remaining / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-left">
          <div className="app-title">
            <Trophy className="title-icon" size={24} />
            <div className="title-text">
              <h1 className="main-title">{getSessionTitle()}</h1>
              <p className="subtitle">{getSessionSubtitle()}</p>
            </div>
          </div>
        </div>
        
        <div className="header-center">
          {showTimer() && (
            <div className="session-timer">
              <Timer size={18} />
              <span className="timer-text">{getTimeRemaining()}</span>
            </div>
          )}
        </div>
        
        <div className="header-right">
          <div className="current-time">
            {formatTime(currentTime)}
          </div>
          
          {onReset && (
            <button 
              onClick={onReset}
              className="reset-button"
              title="Reset Session"
            >
              <RotateCcw size={18} />
              Reset
            </button>
          )}
        </div>
      </div>
      
      <style jsx>{`
        .app-header {
          background: linear-gradient(135deg, rgba(30, 58, 138, 0.95) 0%, rgba(59, 130, 246, 0.95) 100%);
          backdrop-filter: blur(10px);
          border-bottom: 2px solid rgba(222, 255, 1, 0.3);
          padding: 16px 24px;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
          gap: 20px;
        }

        .header-left {
          justify-self: start;
        }

        .header-center {
          justify-self: center;
        }

        .header-right {
          justify-self: end;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .app-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .title-icon {
          color: #deff01;
          flex-shrink: 0;
        }

        .title-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .main-title {
          color: white;
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0;
          line-height: 1.2;
        }

        .subtitle {
          color: rgba(222, 255, 1, 0.9);
          font-size: 0.875rem;
          margin: 0;
          font-weight: 500;
        }

        .session-timer {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(239, 68, 68, 0.2);
          color: #fca5a5;
          padding: 8px 16px;
          border-radius: 20px;
          border: 1px solid rgba(239, 68, 68, 0.4);
          font-weight: 600;
          font-size: 0.9rem;
        }

        .timer-text {
          font-family: 'Monaco', 'Menlo', monospace;
          letter-spacing: 0.5px;
        }

        .current-time {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.9rem;
          font-weight: 500;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .reset-button {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .reset-button:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        .reset-button:active {
          transform: translateY(0);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .app-header {
            padding: 12px 16px;
          }

          .header-content {
            grid-template-columns: 1fr auto;
            grid-template-rows: auto auto;
            gap: 12px;
          }

          .header-left {
            grid-column: 1 / -1;
            justify-self: center;
          }

          .header-center {
            grid-column: 1;
            justify-self: start;
          }

          .header-right {
            grid-column: 2;
            justify-self: end;
          }

          .main-title {
            font-size: 1.25rem;
          }

          .subtitle {
            font-size: 0.8rem;
          }

          .reset-button {
            padding: 6px 8px;
            font-size: 0.8rem;
          }

          .current-time {
            font-size: 0.8rem;
          }
        }

        @media (max-width: 480px) {
          .header-content {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .header-left,
          .header-center,
          .header-right {
            justify-self: center;
          }

          .header-right {
            flex-direction: column;
            gap: 8px;
          }

          .app-title {
            justify-content: center;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;